import { useEffect, useState } from 'react';
import type { Step } from 'react-joyride';
import Joyride from 'react-joyride';
import { useIsClient } from 'usehooks-ts';

import { setTourProps } from '@store/actions/action-tour';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  TOUR_STYLE_OPTIONS,
  GLOBAL_TOUR_ROUTE,
  TOUR_LAST_BUTTON_TEXT,
  DEFAULT_TOUR_PROPS,
  ROUTE_CONTEXT_SEGMENTS_TO_REMOVE,
} from '@features/app-tour/app-tour.const';
import type {
  TourConfig,
  StepsConfig,
  TourRoutes,
  TourPathProps,
  AppTourProps,
} from '@features/app-tour/app-tour.models';

import { useTourConfig } from './hooks';

// Main view component
const AppTour = (props: AppTourProps) => {
  const dispatch = useAppDispatch();
  const app = useAppSelector(state => state.app);

  const isClient = useIsClient();

  // Remove context from the route mask
  let { routeMask = GLOBAL_TOUR_ROUTE } = props;
  const { hash = '' } = props;

  // Process routeMask as a string.
  const routeMaskTmp = routeMask.split('/');
  routeMaskTmp.splice(0, ROUTE_CONTEXT_SEGMENTS_TO_REMOVE);
  routeMask = `/${routeMaskTmp.join('/')}`;

  // Get the tour route and executable configuration object for each route
  const tourConfig = useTourConfig();

  // Get the app tour properties object
  const { tourProps } = app.tour;

  // Current path string (including hash)
  const [path, setPath] = useState<string | false>(false);

  // Configuration object for the current route
  const [config, setConfig] = useState<StepsConfig[] | false>(false);

  // Identity of the currently active step definition list
  const [stepId, setStepId] = useState<string | false>(false);

  // Step definition list
  const [steps, setSteps] = useState<Step[] | false>(false);

  /**
   * Decides if the tour should be active or paused (it's necessary to do this
   * before a state transition to get the component to reinitialize).
   */
  const [run, setRun] = useState<boolean>(false);

  // Force component re-mount to fix beacon positioning
  const [mountKey, setMountKey] = useState(0);

  /**
   * Slightly fiddly this bit. We always want to show the main tour above any of
   * route-based tours, so if the global tour is enabled, we need to ensure that
   * the route mask is set to the global tour.
   */
  routeMask =
    tourProps && tourProps?.[GLOBAL_TOUR_ROUTE]?.isDisabled === false
      ? GLOBAL_TOUR_ROUTE
      : routeMask;

  // Calculate `routeMaskWithHash` after potentially changing `routeMask` to
  // the global tour
  const routeMaskWithHash = hash ? `${routeMask}${hash}` : routeMask;

  /**
   * Get the tour data (if any) the corresponds to the current path from the
   * tour props object, or show the global tour if the prop for that path
   * (GLOBAL_TOUR_ROUTE) is not disabled.
   */
  let tourPropsPath: TourPathProps | undefined;

  if (tourProps) {
    // Always check the global tour first as it takes precedence
    if (tourProps[GLOBAL_TOUR_ROUTE]?.isDisabled === false) {
      tourPropsPath = tourProps[GLOBAL_TOUR_ROUTE];
    } else {
      // Only check for path-specific tours if global tour is disabled
      const pathToCheck = path || routeMaskWithHash;
      if (pathToCheck) {
        const pathProps = tourProps[pathToCheck as TourRoutes];
        if (
          pathProps &&
          typeof pathProps === 'object' &&
          'isDisabled' in pathProps
        ) {
          tourPropsPath = pathProps as TourPathProps;
        }
      }
    }
  }

  // If the route has changed then reset the path hook and other base conditions
  useEffect(() => {
    if (routeMaskWithHash !== path) {
      setPath(routeMaskWithHash || false);
      setConfig(false);
      setStepId(false);
      setSteps(false);
      setRun(true);
    }
  }, [routeMaskWithHash, path]);

  /**
   * Find the correct `steps` list that corresponds to the current step ID and
   * then enable the `run` attribute on the tour component. Null the current
   * steps object if no match is found.
   */
  useEffect(() => {
    if (config && !run && tourPropsPath) {
      const stepObj = config?.find(item => item.id === stepId);

      if (stepObj) {
        let arr = stepObj.steps;

        /**
         * Filter out any introductory steps if introductions are disabled for
         * this tour
         */
        if (!tourPropsPath.showIntro) {
          arr = arr.filter(item => !item.data?.intro);
        }
        setSteps(arr);
        setRun(true);
      } else {
        setSteps(false);
      }
    }
  }, [tourPropsPath && !tourPropsPath.isDisabled, config]);

  /**
   * Fix beacon positioning by forcing a re-render after the initial load. This
   * addresses an issue where beacons are incorrectly positioned when content
   * loads dynamically. Joyride does not automatically recalculate (at least,
   * not properly), so we have to...
   */
  useEffect(() => {
    if (run && steps && isClient) {
      // Wait for content to stabilize, then force re-mount of Joyride
      const timer = setTimeout(() => {
        setMountKey(prev => prev + 1);
      }, 600);

      // Also, listen for window resize to trigger recalculation
      const handleResize = () => {
        setMountKey(prev => prev + 1);
      };

      // Debounce resize handler
      let resizeTimer: NodeJS.Timeout;
      const debouncedResize = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(handleResize, 100);
      };

      window.addEventListener('resize', debouncedResize);

      return () => {
        clearTimeout(timer);
        clearTimeout(resizeTimer);
        window.removeEventListener('resize', debouncedResize);
      };
    }
  }, [run, steps, isClient]);

  /**
   * The following condition safely bootstraps the `config` and `path`
   * properties when the page is initialized (the `config` object should be
   * `false` when this happens).
   */
  if (!config) {
    // First, try to find a config for the route with hash
    let baseConfig =
      typeof tourConfig === 'object' &&
      tourConfig[routeMaskWithHash as keyof TourConfig] instanceof Array &&
      tourConfig[routeMaskWithHash as keyof TourConfig].length
        ? tourConfig[routeMaskWithHash as keyof TourConfig]
        : false;

    // If no hash-specific config found, fall back to the route without hash
    if (!baseConfig && hash) {
      baseConfig =
        typeof tourConfig === 'object' &&
        tourConfig[routeMask as keyof TourConfig] instanceof Array &&
        tourConfig[routeMask as keyof TourConfig].length
          ? tourConfig[routeMask as keyof TourConfig]
          : false;
    }

    // If the `baseConfig` was retrieved for this path, then proceed
    if (baseConfig) {
      setPath(routeMaskWithHash);
      setConfig(baseConfig);
    }
  }

  /**
   * When a path and a config is available, the steps associated with the
   * current route can be determined
   */
  if (path && config && tourProps) {
    /**
     * If the currently stored set of steps no longer correlates with the
     * calculated step object, pause the tour component and switch the step ID
     * so that it matches the new set
     */
    if (stepId !== config[0].id) {
      setRun(false);
      setStepId(config[0].id);
    }
  }

  // Strange voodoo causes the Policy App Tour to persist and break test runs.
  if (!isClient) return null;

  /**
   * If the current runtime is the browser and steps have been found that
   * correspond to the current state, render the component
   */
  return steps &&
    stepId &&
    tourProps &&
    tourPropsPath &&
    !tourPropsPath.isDisabled ? (
    <Joyride
      key={`${stepId}-${mountKey}`}
      run={run}
      continuous
      styles={TOUR_STYLE_OPTIONS}
      steps={steps}
      locale={{ last: TOUR_LAST_BUTTON_TEXT }}
      showSkipButton
      disableScrolling
      callback={data => {
        if (
          tourPropsPath &&
          (data.action === 'reset' ||
            (data.action === 'close' && data.lifecycle === 'complete'))
        ) {
          // Default props to apply when the tour is closed
          let tourPropsOnClose: Record<string, TourPathProps> = {
            [routeMask]: DEFAULT_TOUR_PROPS,
          };

          /**
           * The tour might want to change the state of other tours that are
           * mapped to equivalent path (for example, `/reports` and
           * `/reports/new`), so this block of code will check for any
           * `overrideTourPropsOnClose` data and apply it to the tour props
           * on close
           */
          if (config && config[0]?.data?.overrideTourPropsOnClose) {
            tourPropsOnClose = config[0].data
              .overrideTourPropsOnClose as Record<string, TourPathProps>;
          }

          dispatch(
            setTourProps({
              ...tourProps,
              ...tourPropsOnClose,
            }),
          );
        }
      }}
    />
  ) : null;
};

export default AppTour;
